import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * LanguageSelect – Dropdown custom amb banderes SVG (CA/ES/EN/FR/PT)
 * Banderes específiques per país:
 *   CA: Andorra, ES: España, EN: UK, FR: França, PT: Portugal
 */

const LABEL = {
    ca: "Català",
    es: "Español", 
    en: "English",
    fr: "Français",
    pt: "Português",
};

// Banderas SVG para cada país
const FLAGS = {
  ca: (
    // Bandera de Andorra - Tricolor vertical azul-amarillo-rojo con escudo
    <svg width="20" height="15" viewBox="0 0 20 15" fill="none">
      {/* Franjas verticales */}
      <rect width="5.5" height="15" fill="#0018A8"/> {/* Azul */}
      <rect x="5.5" width="9" height="15" fill="#FFD700"/> {/* Amarillo (más ancho) */}
      <rect x="14.5" width="5.5" height="15" fill="#C8102E"/> {/* Rojo */}
      
      {/* Escudo de Andorra en el centro */}
      <g transform="translate(7.5, 2.5)">
        {/* Escudo principal */}
        <path d="M0 0L5 0L5 10L0 10Z" fill="none" stroke="#8B4513" strokeWidth="0.3"/>
        
        {/* Cuarteles del escudo */}
        {/* Superior izquierdo - Obispo de Urgell */}
        <rect x="0.2" y="0.2" width="2.3" height="4.8" fill="#C8102E"/>
        <path d="M1.1 1.5L1.3 2L1.5 1.5L1.3 1L1.1 1.5" fill="#FFD700"/>
        <path d="M1.3 1.8L1.1 2.2L1.3 2.6L1.5 2.2L1.3 1.8" fill="#FFD700"/>
        
        {/* Superior derecho - Conde de Foix */}
        <rect x="2.5" y="0.2" width="2.3" height="4.8" fill="#FFD700"/>
        <rect x="2.7" y="0.4" width="0.5" height="4.4" fill="#C8102E"/>
        <rect x="3.5" y="0.4" width="0.5" height="4.4" fill="#C8102E"/>
        <rect x="4.3" y="0.4" width="0.3" height="4.4" fill="#C8102E"/>
        
        {/* Inferior izquierdo - Cataluña */}
        <rect x="0.2" y="5" width="2.3" height="4.8" fill="#FFD700"/>
        <rect x="0.4" y="5.2" width="0.3" height="4.4" fill="#C8102E"/>
        <rect x="0.9" y="5.2" width="0.3" height="4.4" fill="#C8102E"/>
        <rect x="1.4" y="5.2" width="0.3" height="4.4" fill="#C8102E"/>
        <rect x="1.9" y="5.2" width="0.4" height="4.4" fill="#C8102E"/>
        
        {/* Inferior derecho - Vizconde de Béarn */}
        <rect x="2.5" y="5" width="2.3" height="4.8" fill="#FFD700"/>
        <ellipse cx="3.1" cy="6.5" rx="0.4" ry="0.6" fill="#8B4513"/>
        <ellipse cx="3.1" cy="8.5" rx="0.4" ry="0.6" fill="#8B4513"/>
        <circle cx="2.9" cy="6.3" r="0.1" fill="#FFD700"/>
        <circle cx="3.3" cy="6.3" r="0.1" fill="#FFD700"/>
        <circle cx="2.9" cy="8.3" r="0.1" fill="#FFD700"/>
        <circle cx="3.3" cy="8.3" r="0.1" fill="#FFD700"/>
      </g>
    </svg>
  ),
  es: (
    // Bandera de España
    <svg width="20" height="15" viewBox="0 0 20 15" fill="none">
      <rect width="20" height="3.75" fill="#C60B1E"/>
      <rect y="3.75" width="20" height="7.5" fill="#FFC400"/>
      <rect y="11.25" width="20" height="3.75" fill="#C60B1E"/>
      <g transform="translate(4, 6)">
        <rect width="8" height="3" fill="#AA151B"/>
        <rect x="1" y="0.5" width="6" height="2" fill="#F1BF00"/>
      </g>
    </svg>
  ),
  en: (
    // Bandera del Reino Unido
    <svg width="20" height="15" viewBox="0 0 20 15" fill="none">
      <rect width="20" height="15" fill="#012169"/>
      <g>
        <path d="M0 0L20 15M20 0L0 15" stroke="white" strokeWidth="2"/>
        <path d="M0 0L20 15M20 0L0 15" stroke="#C8102E" strokeWidth="1"/>
        <path d="M10 0V15M0 7.5H20" stroke="white" strokeWidth="3"/>
        <path d="M10 0V15M0 7.5H20" stroke="#C8102E" strokeWidth="2"/>
      </g>
    </svg>
  ),
  fr: (
    // Bandera de Francia
    <svg width="20" height="15" viewBox="0 0 20 15" fill="none">
      <rect width="6.67" height="15" fill="#002395"/>
      <rect x="6.67" width="6.67" height="15" fill="white"/>
      <rect x="13.33" width="6.67" height="15" fill="#ED2939"/>
    </svg>
  ),
  pt: (
    // Bandera de Portugal
    <svg width="20" height="15" viewBox="0 0 20 15" fill="none">
      <rect width="8" height="15" fill="#046A38"/>
      <rect x="8" width="12" height="15" fill="#DA020E"/>
      <circle cx="8" cy="7.5" r="3" fill="#FFE900" stroke="white" strokeWidth="0.5"/>
      <g transform="translate(6.5, 6)">
        <rect width="3" height="3" fill="#046A38"/>
        <circle cx="1.5" cy="1.5" r="0.8" fill="white"/>
      </g>
    </svg>
  ),
};

const LanguageSelector = () => {
  const { currentLanguage, changeLanguage } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) && 
          buttonRef.current && !buttonRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    // Close dropdown on escape key
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  return (
    <div className="language-selector-modern">
      <button 
        ref={buttonRef}
        className="language-button-modern"
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setIsOpen(!isOpen);
          }
        }}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={`Seleccionar idioma. Idioma actual: ${LABEL[currentLanguage]}`}
      >
        <div className="flag-container-modern">
          {FLAGS[currentLanguage]}
        </div>
        <span className="language-label">{LABEL[currentLanguage]}</span>
        <ChevronDown size={12} className={`chevron-icon ${isOpen ? 'rotated' : ''}`} />
      </button>

      {isOpen && (
        <div 
          ref={dropdownRef} 
          className="language-dropdown-modern"
          role="listbox"
          aria-label="Seleccionar idioma"
        >
          {Object.entries(LABEL).map(([code, name]) => (
            <button
              key={code}
              className={`language-option-modern ${currentLanguage === code ? 'active' : ''}`}
              onClick={() => {
                changeLanguage(code);
                setIsOpen(false);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  changeLanguage(code);
                  setIsOpen(false);
                }
              }}
              role="option"
              aria-selected={currentLanguage === code}
              aria-label={`Cambiar idioma a ${name}`}
            >
              <div className="flag-container-modern">
                {FLAGS[code]}
              </div>
              <span className="language-option-label">{name}</span>
              {currentLanguage === code && (
                <div className="active-indicator"></div>
              )}
            </button>
          ))}
        </div>
      )}

      <style>{`
        .language-selector-modern {
          position: relative;
          width: 100%;
        }

        .language-button-modern {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.6rem 0.8rem;
          background: #ffffff;
          border: 1px solid rgba(12, 44, 108, 0.12);
          border-radius: 8px;
          font-size: 0.75rem;
          font-weight: 500;
          color: #0c2c6c;
          cursor: pointer;
          transition: all 0.4s ease;
          width: 100%;
          backdrop-filter: none;
          position: relative;
          overflow: hidden;
        }

        .language-button-modern:hover {
          background: #ffffff;
          border-color: rgba(244, 111, 66, 0.6);
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(244, 111, 66, 0.2);
        }

        .language-button-modern:active {
          transform: translateY(0);
        }

        .flag-container-modern {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 18px;
          height: 13px;
          border-radius: 2px;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
          flex-shrink: 0;
        }

        .language-label {
          flex: 1;
          text-align: left;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          color: #0c2c6c;
        }

        .chevron-icon {
          color: #f46f42;
          transition: all 0.4s ease;
          flex-shrink: 0;
        }

        .chevron-icon.rotated {
          transform: rotate(180deg);
          color: #f46f42;
        }

        .language-dropdown-modern {
          position: absolute;
          top: 0;
          left: calc(100% + 8px);
          width: 180px;
          background: #ffffff;
          border: 1px solid rgba(12, 44, 108, 0.15);
          border-radius: 8px;
          box-shadow: 0 8px 24px rgba(12, 44, 108, 0.15);
          backdrop-filter: none;
          z-index: 1002;
          overflow: hidden;
          animation: dropdownFadeInRight 0.3s ease-out;
          max-height: 250px;
          overflow-y: auto;
        }

        @keyframes dropdownFadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes dropdownFadeInRight {
          from {
            opacity: 0;
            transform: translateX(-10px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        .language-option-modern {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          width: 100%;
          padding: 0.6rem 0.8rem;
          background: none;
          border: none;
          text-align: left;
          font-size: 0.75rem;
          font-weight: 500;
          color: #0c2c6c;
          cursor: pointer;
          transition: all 0.3s ease;
          position: relative;
          border-bottom: 1px solid rgba(12, 44, 108, 0.06);
        }

        .language-option-modern:last-child {
          border-bottom: none;
        }

        .language-option-modern:hover {
          background: rgba(244, 111, 66, 0.08);
          color: #0c2c6c;
        }

        .language-option-modern.active {
          background: rgba(244, 111, 66, 0.12);
          color: #0c2c6c;
          border-left: 3px solid #f46f42;
        }

        .language-option-label {
          flex: 1;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .active-indicator {
          width: 6px;
          height: 6px;
          background: #f46f42;
          border-radius: 50%;
          flex-shrink: 0;
          animation: pulse 2s ease-in-out infinite;
        }

        @keyframes pulse {
          0%, 100% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 0.7;
            transform: scale(1.2);
          }
        }

        /* Responsive adjustments */
        @media (max-width: 768px) {
          .language-button-modern {
            padding: 0.5rem 0.6rem;
            font-size: 0.7rem;
          }
          
          .language-option-modern {
            padding: 0.5rem 0.6rem;
            font-size: 0.7rem;
          }
          
          .flag-container-modern {
            width: 16px;
            height: 12px;
          }

          .language-dropdown-modern {
            left: auto !important;
            right: 0 !important;
            width: 160px !important;
          }
        }

        /* Para pantallas muy pequeñas */
        @media (max-width: 480px) {
          .language-dropdown-modern {
            left: auto !important;
            right: 0 !important;
            width: 140px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default LanguageSelector;